import * as yup from "yup";
import {
  validationMessages,
  validationMessagesForBotChatSettings,
  validationMessagesForCms,
  validationMessagesForCoupon,
  validationMessagesForPlan,
  validationMessagesForUser,
} from "./constant";
import validationUtility from "./Validation";
import { parsePhoneNumber } from "awesome-phonenumber";

const validUrl = (url) => {
  return validationUtility.validUrl(url);
};

const businessUrlHostnamePattern =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const isValidBusinessUrl = (value) => {
  if (!value) {
    return true;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue || !validUrl(trimmedValue)) {
    return false;
  }

  try {
    const parsedUrl = new URL(trimmedValue);
    const { hostname, protocol } = parsedUrl;
    const isHttpProtocol = protocol === "http:" || protocol === "https:";
    const hasDomainLikeHost =
      businessUrlHostnamePattern.test(hostname) || hostname === "localhost";

    return isHttpProtocol && hasDomainLikeHost;
  } catch (error) {
    return false;
  }
};

const passPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const emailPattern =
  /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,4}[a-zA-Z]{2,10}$/;

export const registrationSchema = yup.object({
  firstName: yup
    .string()
    .transform((value) => (!!value.trim() ? value.trim().toLowerCase() : ""))
    .required(validationMessages.firstName),
  lastName: yup
    .string()
    .transform((value) => (!!value.trim() ? value.trim().toLowerCase() : ""))
    .required(validationMessages.lastName),
  email: yup
    .string()
    .transform((value) => (!!value.trim() ? value.trim().toLowerCase() : ""))
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  // phoneNumber: yup
  //   .string()
  //   .required(validationMessages.phoneRequired)
  //   .test("is_valid_phone", validationMessages.phoneInvalid, (value) => {
  //     if (!value) return false;
  //     return parsePhoneNumber(`${value}`).valid;
  //   }),
  phoneNumber: yup
    .string()
    .nullable()
    .test("is-valid-phone", validationMessages.phoneInvalid, function (value) {
      if (!value || value.trim() === "") return true;
      return parsePhoneNumber(`${value}`).valid;
    }),
  password: yup
    .string()
    .min(8, validationMessages.passwordErr)
    .required(validationMessages.passwordRequired)
    .matches(passPattern, validationMessages.passwordPatternErr),
  confirmPassword: yup
    .string()
    .oneOf(
      [yup.ref("password"), null],
      validationMessages.confirmPasswordMismatch,
    )
    .required(validationMessages.confirmPasswordRequired),

  businessUrl: yup
    .string()
    .nullable()
    .test("valid-url", validationMessages.validUrl, (value) =>
      isValidBusinessUrl(value),
    ),

  organizationName: yup.string().nullable(),

  promoCode: yup.string().nullable(),

  isAgree: yup
    .boolean()
    .oneOf([true], "You must accept the Terms of Service and Privacy Policy.")
    .required("You must accept the Terms of Service and Privacy Policy."),
});

export const loginSchema = yup.object({
  email: yup
    .string()
    .transform((value) =>
      typeof value === "string" ? value.trim().toLowerCase() : value,
    )
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),

  password: yup
    .string()
    // .min(8, validationMessages.passwordErr)
    .required(validationMessages.passwordRequired),
  // .matches(passPattern, validationMessages.passwordErr),
});

const validUrlWithoutScheme =
  /^((?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(?:\/[^\s]*)?$/;

export const botChatSettingsSchema = yup.object().shape({
  chatbotDescription: yup
    .string()
    .required(validationMessagesForBotChatSettings.chatbotDescription),
  tone: yup
    .string()
    .required(validationMessagesForBotChatSettings.toneRequired),
  language: yup
    .string()
    .required(validationMessagesForBotChatSettings.languageRequired),
  fontHeader: yup
    .string()
    .required(validationMessagesForBotChatSettings.fontHeaderRequired),
  fontBody: yup
    .string()
    .required(validationMessagesForBotChatSettings.fontBodyRequired),
  responseLength: yup
    .string()
    .required(validationMessagesForBotChatSettings.responseLengthRequired),
  // domainLink: yup
  //   .string()
  //   .url(validationMessagesForBotChatSettings.domainLinkInvalid)
  //   .required(validationMessagesForBotChatSettings.domainLinkRequired),
  domainLink: yup
    .string()
    .matches(
      validUrlWithoutScheme,
      validationMessagesForBotChatSettings.domainLinkInvalid,
    )
    .required(validationMessagesForBotChatSettings.domainLinkRequired),
});

export const couponSchema = yup.object().shape({
  // expiresAt: yup
  //   .date()
  //   .nullable()
  //   .transform((value, originalValue) =>
  //     originalValue === "" ? null : value
  //   )
  //   .when('couponDuration', {
  //     is: (value) => ['repeating'].includes(value), // Condition
  //     then: (schema) => schema.required(validationMessagesForCoupon.expiresAtRequired),
  //     otherwise: (schema) => schema.notRequired(),
  //   }),
  couponDurationInMonths: yup
    .number(validationMessagesForCoupon.couponDurationInMonthsvalid)
    .min(1, "Minimun duration of the coupon in months should be 1")
    .when("couponDuration", {
      is: (value) => ["repeating"].includes(value), // Condition
      then: (schema) =>
        schema.required(
          validationMessagesForCoupon.couponDurationInMonthsRequired,
        ),
      otherwise: (schema) => schema.strip(),
    }),
  couponName: yup
    .string()
    .trim()
    .required(validationMessagesForCoupon.couponNameRequired)
    .matches(
      /^[a-zA-Z0-9\-_]+$/,
      validationMessagesForCoupon.couponNameInvalid, // 👈 your custom message
    ),
  couponDuration: yup
    .string()
    .oneOf(
      ["forever", "once", "repeating"],
      validationMessagesForCoupon.couponDurationInvalid,
    )
    .required(validationMessagesForCoupon.couponDurationRequired),

  // discountType: yup
  //   .number()
  //   .transform((value, originalValue) =>
  //     originalValue === "" ? null : value
  //   )
  //   .oneOf([1, 2], validationMessagesForCoupon.discountTypeInvalid)
  //   .required(validationMessagesForCoupon.discountTypeRequired),
  // couponAmount: yup
  //   .number()
  //   .transform((value, originalValue) =>
  //     originalValue === "" ? null : value
  //   )
  //   .positive(validationMessagesForCoupon.couponAmountPositive)
  //   .required(validationMessagesForCoupon.couponAmountRequired),

  discountType: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .oneOf([1, 2], validationMessagesForCoupon.discountTypeInvalid)
    .required(validationMessagesForCoupon.discountTypeRequired),
  couponAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when("discountType", {
      is: 1, // If discountType is Percent
      then: (schema) =>
        schema
          .min(1, "Min discount type value should be 1") // Min value for Percent
          .max(100, "Max discount type value should be 100"), // Max value for Percent
      otherwise: (schema) =>
        schema.positive(validationMessagesForCoupon.couponAmountPositive), // For Fixed discount type
    })
    .required(validationMessagesForCoupon.couponAmountRequired),

  currency: yup.string().required(validationMessagesForCoupon.currencyRequired),
  // isEligibleForFirstTime: yup.boolean().required(validationMessagesForCoupon.isEligibleRequired),
  expires: yup
    .number()
    .min(0, "Invalid value")
    .test(
      "at-least-one",
      "At least one of years, months, weeks, or days must be greater than 0",
      function (_, context) {
        const { years, months, weeks, days, couponDuration } = context.parent;
        if (["once", "repeating"].includes(couponDuration)) {
          return (
            (years || 0) > 0 ||
            (months || 0) > 0 ||
            (weeks || 0) > 0 ||
            (days || 0) > 0
          );
        }
        return true;
      },
    ),
});

export const planSchema = yup.object().shape({
  planName: yup
    .string()
    .trim()
    .required(validationMessagesForPlan.planNameRequired),

  price: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    // .positive(validationMessagesForPlan.pricePositive)
    .required(validationMessagesForPlan.priceRequired),

  noOfBots: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    // .positive(validationMessagesForPlan.pricePositive)
    .required(validationMessagesForPlan.noOfBotsRequired),

  messageCount: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    // .positive(validationMessagesForPlan.pricePositive)
    .required(validationMessagesForPlan.messageCountRequired),

  currency: yup.string().required(validationMessagesForPlan.currencyRequired),

  isFreePlan: yup.boolean(),

  // noOfDay: yup
  // .string()
  // .when('isFreePlan', {
  //   is: true,
  //   then: (schema) => schema
  //     .trim()
  //     .required(validationMessagesForPlan.noOfDayRequired),
  //   otherwise: (schema) => schema
  //     .nullable()
  // }),

  noOfDay: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when("isFreePlan", {
      is: true,
      then: (schema) =>
        schema
          .positive(validationMessagesForPlan.noOfDayMustBeNumber)
          .required(validationMessagesForPlan.noOfDayRequired),
      otherwise: (schema) => schema.nullable(),
    }),

  module: yup
    .array()
    .min(1, validationMessagesForPlan.planDescriptionMustBeOne)
    .required(validationMessagesForPlan.planDescriptionRequired),
  subscriptionType: yup
    .string()
    .required(validationMessagesForPlan.planSubscriptionType),
});

export const cmsSchema = yup.object().shape({
  title: yup.string().when("cmsType", {
    is: (cmsType) => cmsType !== "15" && cmsType !== "16",
    then: (schema) => schema.required(validationMessagesForCms.title),
    otherwise: (schema) => schema.notRequired(),
  }),

  // files: yup.mixed().when("fileType", {
  //   is: (fileType) => fileType == "video/mp4",
  //   then: (schema) => schema.required("Please upload a valid video file"),
  //   otherwise: (schema) => schema.notRequired(),
  // }),

  fileLink: yup.string().when("fileType", {
    is: (fileType) => fileType == "youtube",
    then: (schema) => schema.required("Please enter valid Youtube link"),
    otherwise: (schema) => schema.notRequired(),
  }),
  // subTitle: yup.string().required(validationMessagesForCms.subTitle),
  description: yup
    .string()
    .test("is-not-empty", validationMessagesForCms.description, (value) => {
      return value && value.trim() !== "<p><br></p>"; // Ensure it's not just an empty editor
    }),
  // description: yup.string().required(validationMessagesForCms.description),
  cmsType: yup.string().required(validationMessagesForCms.cmsType),
  // fileType: yup.string().required("Video type required"),
});

const today = new Date();
today.setHours(0, 0, 0, 0);

export const userSchema = yup.object().shape({
  firstName: yup.string().required(validationMessagesForUser.firstName),
  lastName: yup.string().required(validationMessagesForUser.lastName),
  plan: yup.string().required("Plan is required"),
  startDate: yup
    .date()
    .required("Start date is required")
    .min(today, "Start date must be today or future"),

  endDate: yup
    .date()
    .required("End date is required")
    .min(yup.ref("startDate"), "End date must be after start date"),
  // subTitle: yup.string().required(validationMessagesForUser.subTitle),
  email: yup
    .string()
    .email(validationMessagesForUser.emailInValid)
    .required(validationMessagesForUser.email)
    .matches(emailPattern, validationMessages.emailPatternErr),

  phoneNumber: yup
    .string()
    .nullable()
    .test("is-valid-phone", validationMessages.phoneInvalid, function (value) {
      if (!value || value.trim() === "") return true;
      return parsePhoneNumber(`${value}`).valid;
    }),
});
export const contactUsSchema = yup.object().shape({
  firstName: yup.string().required(validationMessages.firstName),
  lastName: yup.string().required(validationMessages.lastName),
  email: yup
    .string()
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  // phone: yup
  //   .string()
  //   .required(validationMessages.phoneRequired)
  //   .test("is_valid_phone", validationMessages.phoneInvalid, (value) => {
  //     if (!value) return false;
  //     return parsePhoneNumber(`${value}`).valid;
  //   }),
  phone: yup
    .string()
    .nullable()
    .test("is-valid-phone", validationMessages.phoneInvalid, function (value) {
      if (!value || value.trim() === "") return true;
      return parsePhoneNumber(`${value}`).valid;
    }),
  subject: yup.string().required(),
  message: yup
    .string()
    .required("Please provide a message to help us understand your query."),
  // agree: yup.boolean().required("Please accept term & condition")
});
export const feedbackSchema = yup.object().shape({
  subject: yup.string().required(),
  message: yup
    .string()
    .required("Please provide a message to help us understand your query."),
  // agree: yup.boolean().required("Please accept term & condition")
});
export const siteConetentSchema = yup.object().shape({
  supportEmail: yup
    .string()
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  billingEmail: yup
    .string()
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  salesEmail: yup
    .string()
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  automatedEmail: yup
    .string()
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  defaultEmail: yup
    .string()
    .email(validationMessages.emailInvalid)
    .required(validationMessages.emailRequired)
    .matches(emailPattern, validationMessages.emailPatternErr),
  phoneNumber: yup
    .string()
    .required(validationMessages.phoneRequired)
    .test("is_valid_phone", validationMessages.phoneInvalid, (value) => {
      if (!value) return false;
      return parsePhoneNumber(`${value}`).valid;
    }),
  // fax: yup
  // .string()
  // .required(validationMessages.phoneRequired)
  // .test("is_valid_phone", validationMessages.phoneInvalid, (value) => {
  //   if (!value) return false;
  //   return new PhoneNumber(`${value}`).isValid();
  // }),
  fax: yup
    .string()
    .required("Fax number is required")
    .matches(/^[\d\s\-+()]+$/, "Invalid fax number format"),
  address: yup.string().required(),

  // agree: yup.boolean().required("Please accept term & condition")
});
